import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineX, HiOutlineShare, HiOutlineDotsVertical } from 'react-icons/hi';
import { useDevice } from '../hooks';

function PWAInstallPrompt() {
  const { t } = useTranslation();
  const { isMobile } = useDevice();
  const [isVisible, setIsVisible] = useState(false);
  const [platform, setPlatform] = useState('');

  useEffect(() => {
    // Check if already in standalone mode (PWA installed)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    if (isStandalone) return;

    // Check if mobile
    if (!isMobile) return;

    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);

    if (isIOS) {
      setPlatform('ios');
    } else if (isAndroid) {
      setPlatform('android');
    } else {
      setPlatform('other');
    }

    // Always show on login screen (after 500ms)
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [isMobile]);

  const handleDismiss = () => {
    setIsVisible(false);
    // No localStorage - will show again on next login
  };

  const handleInstallClick = () => {
    // For Android, try to trigger the install prompt
    if (platform === 'android' && window.deferredPrompt) {
      window.deferredPrompt.prompt();
      window.deferredPrompt.userChoice.then((choiceResult) => {
        window.deferredPrompt = null;
      });
    }
  };

  if (!isVisible) return null;

  return (
    <div className="pwa-install-prompt">
      <button className="pwa-install-prompt__close" onClick={handleDismiss}>
        <HiOutlineX size={20} />
      </button>
      
      <div className="pwa-install-prompt__content">
        {platform === 'ios' && (
          <>
            <div className="pwa-install-prompt__icon">
              <HiOutlineShare size={24} />
            </div>
            <div className="pwa-install-prompt__text">
              <h4>{t('pwa.installTitle')}</h4>
              <p>{t('pwa.iosInstructions')}</p>
            </div>
          </>
        )}
        
        {platform === 'android' && (
          <>
            <div className="pwa-install-prompt__icon">
              <HiOutlineDotsVertical size={24} />
            </div>
            <div className="pwa-install-prompt__text">
              <h4>{t('pwa.installTitle')}</h4>
              <p>{t('pwa.androidInstructions')}</p>
            </div>
          </>
        )}
        
        {platform === 'other' && (
          <div className="pwa-install-prompt__text">
            <h4>{t('pwa.installTitle')}</h4>
            <p>{t('pwa.genericInstructions')}</p>
          </div>
        )}
      </div>
      
      {platform === 'android' && window.deferredPrompt && (
        <button className="pwa-install-prompt__install" onClick={handleInstallClick}>
          {t('pwa.installButton')}
        </button>
      )}
    </div>
  );
}

export default PWAInstallPrompt;
